/* StatementOfActivitiesTab — FASB ASC 958 two-column nonprofit income statement.
 * Splits revenue + expenses + changes in net assets by donor restriction class.
 * Optional prior-period comparison.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileBarChart2, Loader2, RefreshCw, FileSpreadsheet, Calendar, Repeat,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const todayIso = () => new Date().toISOString().slice(0, 10);
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;

const fmt = (n) => {
  const num = Number(n || 0);
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PRESETS = [
  { id: 'mtd', label: 'MTD' },
  { id: 'qtd', label: 'QTD' },
  { id: 'ytd', label: 'YTD' },
  { id: 'last_year', label: 'Last year' },
];

const StatementOfActivitiesTab = () => {
  const [dateFrom, setDateFrom] = useState(firstOfYear());
  const [dateTo, setDateTo] = useState(todayIso());
  const [comparePrior, setComparePrior] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const applyPreset = (id) => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    if (id === 'mtd') { setDateFrom(`${yyyy}-${mm}-01`); setDateTo(todayIso()); }
    if (id === 'qtd') {
      const qStart = new Date(yyyy, Math.floor(now.getMonth() / 3) * 3, 1);
      setDateFrom(qStart.toISOString().slice(0, 10)); setDateTo(todayIso());
    }
    if (id === 'ytd')       { setDateFrom(`${yyyy}-01-01`); setDateTo(todayIso()); }
    if (id === 'last_year') { setDateFrom(`${yyyy - 1}-01-01`); setDateTo(`${yyyy - 1}-12-31`); }
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/finance/statement-of-activities`, {
        params: { date_from: dateFrom, date_to: dateTo, compare_prior: comparePrior },
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load.'); }
    setLoading(false);
  }, [dateFrom, dateTo, comparePrior]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    window.open(`${BACKEND_URL}/api/admin/finance/statement-of-activities.csv?date_from=${dateFrom}&date_to=${dateTo}`, '_blank');
  };

  const cur = data?.current_period;
  const prior = data?.prior_period;

  return (
    <div className="space-y-4" data-testid="statement-of-activities-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <FileBarChart2 className="w-5 h-5 text-[#BA1826]" /> Statement of Activities
          </h2>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
            FASB ASC 958 two-column nonprofit income statement. Shows support, revenue, and expenses
            split by donor restriction. "Net Assets Released from Restrictions" reclassifies dollars
            from the restricted column to unrestricted as NHF spends them on the program they were
            given for. This is the report board members read, grant officers verify, and auditors
            tie back to the Balance Sheet.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load}
            className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
            data-testid="soa-refresh"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button onClick={downloadCsv}
            className="text-xs px-3 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1.5"
            data-testid="soa-csv-btn"><FileSpreadsheet className="w-3 h-3" /> CSV</button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 flex items-center gap-2 flex-wrap"
        data-testid="soa-filters">
        <Calendar className="w-3.5 h-3.5 text-[#BA1826]" />
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="text-xs px-2 py-1 border border-[#E5E7EB] rounded" data-testid="soa-date-from" />
        <span className="text-xs text-[#9CA3AF]">→</span>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="text-xs px-2 py-1 border border-[#E5E7EB] rounded" data-testid="soa-date-to" />
        <div className="flex items-center gap-1 ml-1">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => applyPreset(p.id)}
              className="text-[10px] px-2 py-0.5 border border-[#E5E7EB] rounded hover:bg-[#FAFBFC]"
              data-testid={`soa-preset-${p.id}`}>{p.label}</button>
          ))}
        </div>
        <label className="text-[11px] text-[#4B5563] inline-flex items-center gap-1.5 cursor-pointer ml-3">
          <input type="checkbox" checked={comparePrior}
            onChange={(e) => setComparePrior(e.target.checked)}
            data-testid="soa-compare-prior" />
          <Repeat className="w-3 h-3 text-[#9CA3AF]" /> Compare prior period
        </label>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>}

      {loading || !cur ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="soa-statement">
          <div className="bg-[#0A0A0A] text-white px-3 py-2">
            <p className="font-heading font-bold text-sm">Novena Humanitarian Foundation</p>
            <p className="text-[10px] uppercase tracking-wider opacity-80">
              Statement of Activities — {cur.date_from} to {cur.date_to}
              {prior && <span className="ml-2 opacity-60">vs. {prior.date_from} to {prior.date_to}</span>}
            </p>
          </div>

          <table className="w-full text-xs" data-testid="soa-table">
            <thead className="bg-[#F8F9FA] text-[#9CA3AF]">
              <tr>
                <th className="text-left px-3 py-2"></th>
                <th className="text-right px-3 py-2 w-32">Without Restriction</th>
                <th className="text-right px-3 py-2 w-32">With Restriction</th>
                <th className="text-right px-3 py-2 w-28">Total</th>
                {prior && <th className="text-right px-3 py-2 w-28 text-[#9CA3AF]">Prior Total</th>}
              </tr>
            </thead>
            <tbody>
              <SectionHeader title="Support and Revenue" hasPrior={!!prior} />
              {cur.revenue_rows.map((r, i) => (
                <Row key={`rev-${i}`} label={r.label}
                  without={r.without_restriction} withR={r.with_restriction} total={r.total}
                  prior={prior?.revenue_rows?.find((p) => p.label === r.label)?.total} hasPrior={!!prior}
                  testid={`soa-revenue-${i}`} />
              ))}
              <Row label="Total contributions, gains, and revenue"
                without={cur.revenue_totals.without_restriction}
                withR={cur.revenue_totals.with_restriction}
                total={cur.revenue_totals.total}
                prior={prior?.revenue_totals?.total} hasPrior={!!prior}
                bold subtle testid="soa-revenue-total" />
              {cur.net_assets_released_from_restrictions > 0 && (
                <Row label="Net assets released from restrictions"
                  without={cur.net_assets_released_from_restrictions}
                  withR={-cur.net_assets_released_from_restrictions}
                  total={0}
                  prior={prior?.net_assets_released_from_restrictions} hasPrior={!!prior}
                  italic testid="soa-released" />
              )}
              <Row label="Total support and revenue"
                without={cur.total_support_and_revenue.without_restriction}
                withR={cur.total_support_and_revenue.with_restriction}
                total={cur.total_support_and_revenue.total}
                prior={prior?.total_support_and_revenue?.total} hasPrior={!!prior}
                emphasized testid="soa-total-support" />

              <SectionHeader title="Expenses" hasPrior={!!prior} />
              {cur.expense_rows.length === 0 ? (
                <tr><td colSpan={prior ? 5 : 4} className="px-3 py-2 italic text-[#9CA3AF]">No expenses in this period.</td></tr>
              ) : cur.expense_rows.map((r, i) => (
                <Row key={`exp-${i}`} label={r.label}
                  without={r.amount} withR={0} total={r.amount}
                  prior={prior?.expense_rows?.find((p) => p.functional_class === r.functional_class)?.amount} hasPrior={!!prior}
                  testid={`soa-expense-${r.functional_class}`} />
              ))}
              <Row label="Total expenses"
                without={cur.expense_totals.without_restriction}
                withR={cur.expense_totals.with_restriction}
                total={cur.expense_totals.total}
                prior={prior?.expense_totals?.total} hasPrior={!!prior}
                emphasized testid="soa-total-expenses" />

              <SectionHeader title="Changes in Net Assets" hasPrior={!!prior} />
              <Row label="Change in net assets"
                without={cur.change_in_net_assets.without_restriction}
                withR={cur.change_in_net_assets.with_restriction}
                total={cur.change_in_net_assets.total}
                prior={prior?.change_in_net_assets?.total} hasPrior={!!prior}
                bold testid="soa-change" />
              <Row label="Net assets, beginning of period"
                without={cur.beginning_net_assets.without_restriction}
                withR={cur.beginning_net_assets.with_restriction}
                total={cur.beginning_net_assets.total}
                prior={prior?.beginning_net_assets?.total} hasPrior={!!prior}
                subtle testid="soa-beginning" />
              <Row label="Net assets, end of period"
                without={cur.ending_net_assets.without_restriction}
                withR={cur.ending_net_assets.with_restriction}
                total={cur.ending_net_assets.total}
                prior={prior?.ending_net_assets?.total} hasPrior={!!prior}
                emphasized testid="soa-ending" />
            </tbody>
          </table>

          <p className="px-3 py-2 text-[10px] text-[#9CA3AF] border-t border-[#E5E7EB] bg-[#FAFBFC] leading-relaxed">
            Donor-restricted gifts stay sequestered until NHF spends them on the program they were
            given for. At that point the dollars are reclassified from the restricted column to the
            unrestricted column (the "released" line above). Ending net assets here should match
            the Balance Sheet's Net Assets total for the same as-of date.
          </p>
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ title, hasPrior }) => (
  <tr className="bg-[#FAFBFC] border-t border-[#E5E7EB]">
    <td colSpan={hasPrior ? 5 : 4} className="px-3 py-2">
      <span className="text-[10px] uppercase font-bold tracking-wider text-[#4B5563]">{title}</span>
    </td>
  </tr>
);

const Row = ({ label, without, withR, total, prior, hasPrior, bold, emphasized, italic, subtle, testid }) => (
  <tr className={`border-t border-[#E5E7EB] ${emphasized ? 'bg-[#0A0A0A] text-white' : ''} ${subtle ? 'bg-[#FAFBFC]' : ''}`}
    data-testid={testid}>
    <td className={`px-3 py-1.5 ${italic ? 'italic' : ''} ${bold ? 'font-semibold' : ''}`}>{label}</td>
    <td className={`px-3 py-1.5 text-right font-mono ${bold || emphasized ? 'font-bold' : ''}`}>{fmt(without)}</td>
    <td className={`px-3 py-1.5 text-right font-mono ${bold || emphasized ? 'font-bold' : ''}`}>{fmt(withR)}</td>
    <td className={`px-3 py-1.5 text-right font-mono ${bold || emphasized ? 'font-bold' : ''}`}>{fmt(total)}</td>
    {hasPrior && (
      <td className={`px-3 py-1.5 text-right font-mono text-[11px] ${emphasized ? 'opacity-90' : 'text-[#9CA3AF]'}`}>
        {prior === undefined || prior === null ? '—' : fmt(prior)}
      </td>
    )}
  </tr>
);

export default StatementOfActivitiesTab;
